import "./SectionUsersSidebarPanel.scss";

import clsx from "clsx";
import type { FC } from "react";
import { MouseEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useCopyToClipboard } from "usehooks-ts";

import { SectionRole } from "../../../shared/types/roles";
import { CloseOverlayButton } from "../sidebar/CloseOverlayButton";
import { ContentSidebarHeader } from "../sidebar/ContentSidebarHeader";
import { ContentSidebarMain } from "../sidebar/ContentSidebarMain";
import { OverlayContainer } from "../sidebar/OverlayContainer";

import { SectionRoleCard } from "./SectionRoleCard";
import { DisabledUsersCard } from "./SectionUserCard";

import {
  serverCreateSectionInvite,
  serverDeleteSectionInvite,
  serverGetSectionInvite,
} from "api/api-server-actions";
import { LoadingScreen } from "components/LoadingScreen";
import type { SectionInviteType } from "components/server-types";
import { getSidebarHeader } from "helpers/sidebar";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { Menu, MenuItem } from "mds/components/Menu";
import { showConfirmationModal } from "mds/components/Modal";
import { TextField } from "mds/components/TextField";
import { Tooltip } from "mds/components/Tooltip";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { SquareIconButton } from "mds/components/buttons/SquareIconButton";
import {
  AddPlusIcon,
  DotMenuVerticalIcon,
  DuplicateCopyIcon,
  LongFormPencilIcon,
  PersonClassIcon,
  StarIcon,
  TrashDeleteIcon,
} from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { storeApi, useAppSelector } from "store/index";
import {
  selectCurrentSectionId,
  selectCurrentSectionIsArchived,
  selectCurrentUserId,
  selectEnhancedUsersWithSectionActivityInCurrentSection,
  selectOrgUserByUserIdMap,
} from "store/selectors";
import { useListSelector, useObjectSelector } from "store/store-hooks";
import { toastCreateOperationCatcher, toastLocalizedOperationCatcher } from "utils/alerts";
import { CONTENT_SIDEBAR_TAB_NAME } from "utils/urls";

export const SectionUsersSidebarPanel = () => {
  const sectionId = useAppSelector(selectCurrentSectionId);
  const isArchived = useAppSelector(selectCurrentSectionIsArchived);
  const users = useListSelector(selectEnhancedUsersWithSectionActivityInCurrentSection);
  const { canAuthorSection, canAuthorCourse } = useCapabilities();
  const orgUserMap = useObjectSelector(selectOrgUserByUserIdMap);
  // This makes sure an org admin that's a student in the section can then change themselves back to an instructor
  const canModifyUsers = canAuthorSection || canAuthorCourse;
  const userId = useAppSelector(selectCurrentUserId);

  const currentUserInThisSection = useMemo(
    () => users.find((user) => user.user_id === userId),
    [users, userId],
  );

  const canAddSelfToSection = canAuthorCourse && !currentUserInThisSection;

  const onJoinAsInstructor = useCallback(async () => {
    if (!sectionId || !userId) {
      return;
    }
    try {
      await storeApi.section_users.create(
        {
          section_id: sectionId,
          user_id: userId,
          role: SectionRole.INSTRUCTOR,
        },
        { skipToast: true },
      );
      await storeApi.class_users.create({
        section_id: sectionId,
        user_id: userId,
      });
    } catch (error) {
      toastLocalizedOperationCatcher("join_as_instructor")(error as Error);
    }
  }, [sectionId, userId]);

  const enabledUsers = useMemo(
    () =>
      users.filter((user) => {
        if (user.is_disabled) {
          return false;
        }
        const orgUser = orgUserMap?.[user.user_id];
        if (!orgUser) {
          return true;
        }
        return !orgUser.is_disabled;
      }),
    [users, orgUserMap],
  );

  const disabledUsers = useMemo(
    () =>
      users.filter((user) => {
        if (user.is_disabled) {
          return true;
        }
        const orgUser = orgUserMap?.[user.user_id];
        if (!orgUser) {
          return false;
        }
        return orgUser.is_disabled;
      }),
    [users, orgUserMap],
  );

  const emailAddresses = useMemo(() => {
    const otherUsers = enabledUsers.filter((user) => user.user_id !== userId);
    return otherUsers
      .map((user) => user.email)
      .filter((email) => email) // Filter out any undefined emails
      .join(",");
  }, [enabledUsers, userId]);

  if (!sectionId) {
    return <LoadingScreen />;
  }

  const canSeeSectionAssistants =
    canModifyUsers && users.some((u) => u.role === SectionRole.SECTION_ASSISTANT);

  return (
    <OverlayContainer>
      <ContentSidebarHeader panelType="overlay" visibleOnMobile>
        <IconText
          iconStart={<PersonClassIcon className="text-blue" />}
          text={getSidebarHeader(CONTENT_SIDEBAR_TAB_NAME.Users)}
        />

        <div className="flex items-center justify-end gap-2">
          {canModifyUsers && (
            <Menu
              direction="bottom"
              menuButton={
                <SquareIconButton
                  eventName="Section Users - Options"
                  kind="tertiary"
                  size="s"
                  title={t("common.options")}
                  onClick={(e: MouseEvent) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  <DotMenuVerticalIcon />
                </SquareIconButton>
              }
              menuClassName="min-w-[180px]"
            >
              <MenuItem
                disabled={isArchived}
                eventName="Section Users - Add students"
                onClick={() => onShowInviteStudentModal({ sectionId })}
              >
                <IconText
                  iconStart={<AddPlusIcon />}
                  text={t("user_options_menu.invite_students.button")}
                />
              </MenuItem>

              {emailAddresses && (
                <MenuItem
                  eventName="Section Users - Email all"
                  href={`mailto:${emailAddresses}`}
                  target="_blank"
                  allowPropagation
                >
                  <IconText
                    iconStart={<LongFormPencilIcon />}
                    text={t("user_options_menu.email_all")}
                  />
                </MenuItem>
              )}
            </Menu>
          )}

          <CloseOverlayButton />
        </div>
      </ContentSidebarHeader>

      <ContentSidebarMain bottomPadding={false} className="section-user-sidebar-panel">
        {canAddSelfToSection && (
          <SquareButton className="mb-4" kind="tertiary" size="xs" onClick={onJoinAsInstructor}>
            <span>{t("user_options_menu.join_as_instructor.title")}</span>
            <Tooltip text={t("user_options_menu.join_as_instructor.tooltip")} />
          </SquareButton>
        )}

        <SectionRoleCard
          className="mb-4 mt-2"
          headerIcon={<StarIcon size="xs" />}
          sectionRole={SectionRole.INSTRUCTOR}
          users={enabledUsers}
        />

        {canSeeSectionAssistants && (
          <SectionRoleCard
            className="mb-4 mt-2"
            sectionRole={SectionRole.SECTION_ASSISTANT}
            users={enabledUsers}
          />
        )}

        <SectionRoleCard
          className="mb-4"
          sectionRole={SectionRole.STUDENT}
          users={enabledUsers}
          separateUsersInClassSession
          showEmpty
          showOnlineCount
          startOpen
        />

        {canModifyUsers && <DisabledUsersCard className="mb-4" users={disabledUsers} />}
      </ContentSidebarMain>
    </OverlayContainer>
  );
};

interface InviteStudentModalProps {
  sectionId: string;
}

const onShowInviteStudentModal = (props: InviteStudentModalProps) => {
  showConfirmationModal({
    title: t("user_options_menu.invite_students.title"),
    hideCancelConfirmButtons: true,
    children: (onClose) => <InviteStudentModal {...props} onClose={onClose} />,
  }).catch(toastLocalizedOperationCatcher("show_student_invite_modal"));
};

export const InviteStudentModal: FC<InviteStudentModalProps & { onClose: () => void }> = ({
  sectionId,
  onClose,
}) => {
  const [recentlyCopied, setRecentlyCopied] = useState(false);
  // We separately have controls to hide the modal, because we may need to show the delete invite modal
  // _while_ showing this modal
  const [hideModal, setHideModal] = useState(false);

  const [sectionInvite, setSectionInvite] = useState<SectionInviteType | null>(null);
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const [, copyToClipboard] = useCopyToClipboard();

  const link = `${window.location.origin}/?invite_key=${sectionInvite?.invite_key}`;

  // TODO: Replace with storeApi
  useEffect(() => {
    serverGetSectionInvite(sectionId)
      // We assume there's only one invite per course right now
      .then((invites) => setSectionInvite(invites[0]))
      .catch(setError)
      .finally(() => setLoading(false));
  }, [sectionId]);

  const onDeleteInvite = async () => {
    if (!sectionInvite) {
      return;
    }
    await serverDeleteSectionInvite(sectionInvite.id);
    setSectionInvite(null);
  };

  const onCreateInvite = async () => {
    const invite = await serverCreateSectionInvite(sectionId).catch(
      toastCreateOperationCatcher("invite"),
    );

    setSectionInvite(invite || null);
  };

  const onCopy = async () => {
    await copyToClipboard(link);
    setRecentlyCopied(true);
    setTimeout(() => setRecentlyCopied(false), 2000);
  };

  if (error) {
    return (
      <div className="section-invite-modal px-6 pb-6">
        <h3 className="my-2">{t("user_options_menu.invite_students.error_title")}</h3>
        <div className="body-s">
          {t("user_options_menu.invite_students.error_body", { error_message: error?.message })}
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="section-invite-modal px-6 pb-6">
        <LoadingScreen />
      </div>
    );
  }

  if (!sectionInvite) {
    return (
      <div className="section-invite-modal flex flex-col items-center gap-5">
        <div className="body-s">{t("user_options_menu.invite_students.explanation")}</div>
        <div className="flex w-full justify-end gap-2">
          <SquareButton kind="tertiary" size="m" onClick={onClose}>
            {t("common.cancel")}
          </SquareButton>
          <SquareButton
            eventName="New invitation modal - Confirm"
            kind="accent"
            size="m"
            onClick={() => onCreateInvite()}
          >
            {t("user_options_menu.invite_students.action_create")}
          </SquareButton>
        </div>
      </div>
    );
  }

  return (
    <div
      className={clsx("section-invite-modal flex flex-col items-start", {
        hidden: hideModal,
      })}
    >
      <div className="body-s section-invite-modal-text mb-6">
        {t("user_options_menu.invite_students.label")}
      </div>

      <div className="my-3 mb-6 flex w-full justify-center">
        <img
          alt={t("user_options_menu.invite_students.qr_code_alt_text")}
          className="section-invite-modal-qr-code"
          src={`https://api.qrserver.com/v1/create-qr-code/?size=128x128&data=${link}`}
        />
      </div>

      <div className="mt-3 flex items-center gap-2">
        <SquareIconButton
          eventName="Delete student invitation link"
          kind="caution"
          size="l"
          title={t("user_options_menu.invite_students.action_delete")}
          onClick={() => {
            setHideModal(true);
            showConfirmationModal({
              title: t("user_options_menu.invite_students.delete_title"),
              labelText: t("user_options_menu.invite_students.warning_delete"),
              confirmButtonText: t("user_options_menu.invite_students.action_delete"),
              confirmButtonKind: "caution",
              onClose: () => {
                setHideModal(false);
              },
              onConfirm: () => {
                onDeleteInvite();
                onClose();
              },
            });
          }}
        >
          <TrashDeleteIcon />
        </SquareIconButton>

        <TextField className="section-invite-modal-link" value={link} readOnly />

        <div className="flex items-center">
          <Tooltip
            text={
              recentlyCopied
                ? t("user_options_menu.invite_students.copy_link_success")
                : t("user_options_menu.invite_students.copy_link")
            }
          >
            <SquareButton
              eventName="Copy student invitation link"
              kind="accent"
              size="m"
              title={t("user_options_menu.invite_students.copy_link")}
              onClick={onCopy}
            >
              <IconText
                iconStart={<DuplicateCopyIcon />}
                text={t("user_options_menu.invite_students.copy_link")}
              />
            </SquareButton>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

// Backward-compatible export during rename rollout.
